export interface SummaryInline {
  text: string;
  bold: boolean;
}

export type SummaryBlock =
  | { kind: 'heading'; level: 1 | 2; spans: SummaryInline[] }
  | { kind: 'paragraph'; spans: SummaryInline[] }
  | { kind: 'bullets'; items: SummaryInline[][] };

// The summariser emits these section titles as bare lines with no markdown
// marker, so they have to be recognised by name to render as headings.
const KNOWN_SECTION_TITLES = new Set([
  'summary',
  'meeting summary',
  'session summary',
  'executive summary',
  'key decisions',
  'key items & decisions',
  'key items and decisions',
  'action items',
  'immediate action items',
  'discussion highlights',
  'risks / blockers',
  'risks/blockers',
  'risks and blockers',
  'next steps',
]);

// Transcript text arrives peppered with single-asterisk emphasis such as
// "*Discretion*". Strip the markers but keep the word.
const stripEmphasis = (value: string) =>
  value.replace(/(^|[^*])\*(?!\*)([^*]+)\*(?!\*)/g, '$1$2');

export const parseInlineSpans = (line: string): SummaryInline[] => {
  const spans: SummaryInline[] = [];
  const pattern = /\*\*([^*]+)\*\*/g;
  let cursor = 0;
  let match = pattern.exec(line);

  while (match) {
    if (match.index > cursor) {
      spans.push({ text: stripEmphasis(line.slice(cursor, match.index)), bold: false });
    }
    spans.push({ text: stripEmphasis(match[1]), bold: true });
    cursor = match.index + match[0].length;
    match = pattern.exec(line);
  }

  if (cursor < line.length) {
    spans.push({ text: stripEmphasis(line.slice(cursor)), bold: false });
  }

  return spans.filter(span => span.text.length > 0);
};

const headingLevelFor = (line: string): 1 | 2 | null => {
  const hashMatch = line.match(/^(#{1,6})\s+(.*)$/);
  if (hashMatch) return hashMatch[1].length === 1 ? 1 : 2;

  if (/^\*\*(.+)\*\*:?$/.test(line)) return 2;

  const bare = line.replace(/[:#*]/g, '').trim().toLowerCase();
  if (KNOWN_SECTION_TITLES.has(bare)) {
    return bare === 'meeting summary' || bare === 'summary' ? 1 : 2;
  }

  return null;
};

const headingText = (line: string) =>
  line
    .replace(/^#{1,6}\s+/, '')
    .replace(/^\*\*(.+?)\*\*:?$/, '$1')
    .replace(/:$/, '')
    .trim();

export const parseSummaryBlocks = (summary: string | null): SummaryBlock[] => {
  const lines = (summary || '').split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  const blocks: SummaryBlock[] = [];
  let bullets: SummaryInline[][] | null = null;

  const flushBullets = () => {
    if (bullets && bullets.length) blocks.push({ kind: 'bullets', items: bullets });
    bullets = null;
  };

  for (const line of lines) {
    const bulletMatch = line.match(/^[-•*]\s+(.*)$/);
    if (bulletMatch && !line.startsWith('**')) {
      bullets = bullets || [];
      bullets.push(parseInlineSpans(bulletMatch[1]));
      continue;
    }

    flushBullets();

    const level = headingLevelFor(line);
    if (level) {
      blocks.push({ kind: 'heading', level, spans: parseInlineSpans(headingText(line)) });
      continue;
    }

    blocks.push({ kind: 'paragraph', spans: parseInlineSpans(line) });
  }

  flushBullets();
  return blocks;
};
